// @ts-nocheck
/**
 * Create Notification — SUPERADMIN + SUB_SUPERADMIN gate on "Is General"
 * checkbox; hardcoded status=1 in payload preserved.
 */
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { postApiCall } from '@/service/httpCall';
import { API_NOTIFICATION_CREATE } from '@/service/url';
import { ROUTES, USER_ROLES } from '@/utilities/constant';
import { getUser } from '@/utilities/setting';
import { Button } from '@/components/ui/button';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ArrowLeft, Loader2, Save, Megaphone } from 'lucide-react';

const NotificationCreate = () => {
  const navigate = useNavigate();
  const [type, setType] = useState('');
  const [description, setDescription] = useState('');
  const [isGeneral, setIsGeneral] = useState(false);
  const [loading, setLoading] = useState(false);
  const currentRole = getUser()?.role;
  const canSetGeneral =
    currentRole === USER_ROLES.SUPERADMIN ||
    currentRole === USER_ROLES.SUB_SUPERADMIN;

  const submitForm = () => {
    if (
      !type ||
      !description ||
      type.trim().length === 0 ||
      description.trim().length === 0
    )
      return toast.warning('Fields are required');

    setLoading(true);
    const payload = {
      notification_type: type,
      description,
      status: 1,
      is_general: isGeneral,
    };

    postApiCall(API_NOTIFICATION_CREATE, payload)
      .then(() => {
        toast.success('Notification created');
        setTimeout(() => navigate(`/${ROUTES.NOTIFICATION.LIST}`), 800);
      })
      .catch((err: any) => {
        toast.error(friendlyApiError(err) || 'Something went wrong');
        setLoading(false);
      });
  };

  return (
    <ScrollPage>
      <PageHeader
        title="Create Notification"
        description="Compose a notification that can be assigned to agents or users."
        actions={
          <Button asChild variant="outline">
            <Link to={`/${ROUTES.NOTIFICATION.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
        }
      />

      <div
        className="rounded-2xl border border-border/60 bg-card p-5"
        style={{
          boxShadow:
            '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
        }}
      >
        <div className="mb-4 flex items-center gap-3 border-b border-border/50 pb-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-inset ring-primary/25">
            <Megaphone className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-foreground">New notification</h3>
            <p className="text-[11px] text-muted-foreground">
              Assign to agents / users after creating.
            </p>
          </div>
        </div>

        <form onSubmit={(e) => e.preventDefault()}>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="title" className="text-xs font-semibold text-foreground">
                Title <span className="text-destructive">*</span>
              </label>
              <input
                id="title"
                type="text"
                placeholder="e.g. System Alert"
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="input-embossed h-10 w-full rounded-md px-3 text-sm"
              />
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="description"
                className="text-xs font-semibold text-foreground"
              >
                Description <span className="text-destructive">*</span>
              </label>
              <textarea
                id="description"
                rows={4}
                placeholder="What should the recipient see?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="input-embossed w-full rounded-md p-3 text-sm"
              />
            </div>

            {canSetGeneral && (
              <div className="rounded-lg border border-border/60 bg-muted/30 p-3">
                <label className="flex cursor-pointer items-center gap-3">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isGeneral}
                    onClick={() => setIsGeneral(!isGeneral)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                      isGeneral ? 'bg-emerald-500' : 'bg-muted/70'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                        isGeneral ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                  <div>
                    <div className="text-xs font-semibold text-foreground">
                      Is General Notification
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      General notifications are broadcast to all recipients.
                    </div>
                  </div>
                </label>
              </div>
            )}
          </div>

          <div className="mt-5 flex items-center justify-end gap-2 border-t border-border/50 pt-4">
            <Button asChild variant="outline">
              <Link to={`/${ROUTES.NOTIFICATION.LIST}`}>Cancel</Link>
            </Button>
            <Button
              onClick={submitForm}
              type="button"
              disabled={loading}
              className="gap-1.5"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {loading ? 'Creating…' : 'Create notification'}
            </Button>
          </div>
        </form>
      </div>
    </ScrollPage>
  );
};

export default NotificationCreate;
